import {
  BATCH_NO_ANSWER_MESSAGE,
  hasNoResponse,
  ProcessImportBatchBody,
  ProcessImportBatchItemResult,
  ProcessImportScanBody,
  ProcessImportScanReport,
  ProcessImportScanRow,
  useProcessGitImportBatch,
  useProcessGitImportScan,
} from "@via/api/process-git-import.api.tsx";
import Modal from "@via/components/modal";
import { StepProgress } from "@via/components/step-progress";
import { ModalSize } from "@via/utils/constants.tsx";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import ConsentStep from "@via/pages/projects/pipelines/workflow/components/import-process-from-git/consent-step.tsx";
import PickTableStep from "@via/pages/projects/pipelines/workflow/components/import-process-from-git/pick-table-step.tsx";
import ResultsStep from "@via/pages/projects/pipelines/workflow/components/import-process-from-git/results-step.tsx";
import SourceStep from "@via/pages/projects/pipelines/workflow/components/import-process-from-git/source-step.tsx";

/**
 * Importing processes from a git repository, many at once, under one consent.
 *
 * Four steps: point at a repository (Source), scan it and pick any number of its published
 * process revisions from a table (Pick), consent once over every selected commit and choose the
 * menu group (Consent), then see what the batch actually did, item by item (Results). The step
 * index machinery is copied from `import-via-pipeline-modal.tsx`: a step is never left behind its
 * own data because an effect watching a mutation's success advances it, rather than a raw "Next"
 * click that could outrun a request still in flight.
 *
 * The scan runs once from Source and again whenever the "latest revision only" toggle flips
 * (spec 4.3: the toggle maps onto the scan's `revisions` field, it never filters rows already in
 * hand). The toggle defaults to on, so the first scan asks for `"latest"`. The repository, the
 * credential and the toggle value a scan was requested with are recorded in a ref when it is sent
 * and committed to state only when it succeeds, so the rows on screen, the toggle beside them, and
 * the repository the consent names always describe the same scan: a URL edited while a scan is in
 * flight, or a re-scan that fails, can never leave them disagreeing.
 *
 * Each hook already raises a notification on error (see `process-git-import.api.tsx`); this modal
 * additionally keeps the message on the step that failed, so the step does not just look idle
 * while the notification passes by, and it does not advance.
 */

export type GitProvider = ProcessImportScanBody["provider"];

type ScannedSource = {
  repoUrl: string;
  provider: GitProvider;
  serviceTokenId: number | null;
};

const detailOf = (error: unknown, fallback: string): string => {
  const message = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message ?? fallback;
};

const ImportProcessFromGitModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}): JSX.Element => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Source step.
  const [repoUrl, setRepoUrl] = useState("");
  const [provider, setProvider] = useState<GitProvider>("github");
  // `null` means undecided, `""` means explicitly no credential. Both normalize to sending no
  // credential (see script-assets.tsx), so leaving this `null` is exactly right for a public
  // repository: nothing here defaults it to a credential the user never chose.
  const [serviceTokenId, setServiceTokenId] = useState<string | null>(null);

  // Pick step. `latestOnly` describes the rows in `scanReport`, so it only changes when a scan
  // succeeds; `pendingScan` holds what the in flight scan was asked for until then.
  const [latestOnly, setLatestOnly] = useState(true);
  const pendingScan = useRef<{
    source: ScannedSource;
    latestOnly: boolean;
  } | null>(null);
  const [scannedSource, setScannedSource] = useState<ScannedSource | null>(
    null
  );
  const [scanReport, setScanReport] = useState<ProcessImportScanReport | null>(
    null
  );
  const [selected, setSelected] = useState<ProcessImportScanRow[]>([]);

  // Consent step. Never preselected: this is the fail closed gate the whole wizard exists for.
  const [menuGroupId, setMenuGroupId] = useState<number | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  // Set when a batch ended with NO response (the browser's timeout, or a dropped connection):
  // the server may still be importing, so resending this list could import it twice. Only a new
  // scan, which shows what is already here, clears it.
  const [batchOutcomeUnknown, setBatchOutcomeUnknown] = useState(false);
  // Set synchronously when Import sends a batch, cleared when that batch settles or the wizard
  // resets. `batch.isPending` and the button's `disabled` both come from the render a second
  // click lands on, which a fast double click reaches before React has re-rendered.
  const importInFlight = useRef(false);

  // Results step.
  const [results, setResults] = useState<ProcessImportBatchItemResult[] | null>(
    null
  );

  const scan = useProcessGitImportScan();
  const batch = useProcessGitImportBatch();

  // Back to the source step with nothing downstream of it left over: no scan rows, no selection,
  // no menu group, consent unticked, no results, and both mutations idle. `keepSource` is the
  // only difference between "Run again", which keeps the repository the user just imported from
  // so re-running it is one click, and a reopen, which starts completely fresh.
  const resetWizard = ({ keepSource }: { keepSource: boolean }): void => {
    setCurrentStepIndex(0);
    if (!keepSource) {
      setRepoUrl("");
      setProvider("github");
      setServiceTokenId(null);
    }
    setLatestOnly(true);
    pendingScan.current = null;
    setScannedSource(null);
    setScanReport(null);
    setSelected([]);
    setMenuGroupId(null);
    setConsentChecked(false);
    setBatchOutcomeUnknown(false);
    importInFlight.current = false;
    setResults(null);
    scan.reset();
    batch.reset();
  };

  // `Modal` only unmounts what it wraps; this wizard's own state and the two mutation hooks all
  // live for the component's lifetime regardless of `open`. Without this, reopening after an
  // import left the wizard on the Consent step with the checkbox still ticked: consent satisfied
  // without the consent text ever being shown again, one click from re-importing against a list
  // nobody just read. Resetting on the false to true transition (never on the initial mount,
  // which already starts clean) is what makes the fail closed gate hold regardless of how a
  // caller chooses to mount this modal, rather than depending on some other task remembering to
  // unmount it between opens.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      resetWizard({ keepSource: false });
    }
    wasOpen.current = open;
    // `resetWizard` closes over `scan` and `batch`, which are new mutation objects every render
    // (React Query's own contract), so it is deliberately left out of the dependency array:
    // including it would fire this on every render instead of only on the `open` transition.
  }, [open]);

  const normalizedServiceTokenId = useMemo(
    () =>
      null === serviceTokenId || "" === serviceTokenId
        ? null
        : Number(serviceTokenId),
    [serviceTokenId]
  );

  const steps = useMemo(
    () => [
      { name: "Source", isCompleted: currentStepIndex > 0 },
      { name: "Pick", isCompleted: currentStepIndex > 1 },
      { name: "Consent", isCompleted: currentStepIndex > 2 },
      { name: "Results", isCompleted: currentStepIndex > 3 },
    ],
    [currentStepIndex]
  );

  // Names the process on every result row. The batch response names only what it created, so a
  // skipped or failed row takes the name its selected scan row carried. `repoPath` is unique
  // within a batch (the schema refuses a duplicate), so it is a safe key, and `selected` is the
  // exact list the batch was sent with: nothing changes it between Consent and Results.
  const processNames = useMemo(() => {
    const names = new Map<string, string>();
    selected.forEach(row => {
      if (null !== row.name) {
        names.set(row.repoPath, row.name);
      }
    });
    return names;
  }, [selected]);

  const runScan = (source: ScannedSource, nextLatestOnly: boolean): void => {
    pendingScan.current = { source, latestOnly: nextLatestOnly };
    const body: ProcessImportScanBody = {
      ...source,
      revisions: nextLatestOnly ? "latest" : "all",
    };
    scan.mutate(body);
  };

  const handleScanRepository = (): void => {
    runScan(
      { repoUrl, provider, serviceTokenId: normalizedServiceTokenId },
      latestOnly
    );
  };

  // A re-scan of the repository already on screen, never of whatever the source fields say now.
  const handleLatestOnlyChange = (next: boolean): void => {
    if (null === scannedSource) {
      return;
    }
    runScan(scannedSource, next);
  };

  useEffect(() => {
    if (
      !scan.isSuccess ||
      undefined === scan.data ||
      null === pendingScan.current
    ) {
      return;
    }
    setScannedSource(pendingScan.current.source);
    setLatestOnly(pendingScan.current.latestOnly);
    // Stored once per successful scan, so `rows` keeps one array identity until the next scan:
    // the picker keys its selection by index into this array and clears it when the array changes.
    setScanReport(scan.data);
    setCurrentStepIndex(1);
  }, [scan.isSuccess, scan.data]);

  const handleContinueToConsent = (): void => {
    if (0 === selected.length || scan.isPending) {
      return;
    }
    // Entering Consent always starts unticked, whatever happened before: the consent is over this
    // exact list, so it can never be carried over from another one.
    setConsentChecked(false);
    setCurrentStepIndex(2);
  };

  const handleImport = (): void => {
    if (
      importInFlight.current ||
      batchOutcomeUnknown ||
      !consentChecked ||
      null === menuGroupId ||
      null === scannedSource ||
      0 === selected.length
    ) {
      return;
    }
    const body: ProcessImportBatchBody = {
      ...scannedSource,
      menuGroupId,
      confirmImport: true,
      // The same rows the Consent step listed, each bound to the commit id the scan reported and
      // the user was shown. The server re-verifies each tag still points there and skips the item
      // if it moved.
      items: selected.map(row => ({
        repoPath: row.repoPath,
        revision: row.revId,
        expectedCommitId: row.commitId,
      })),
    };
    importInFlight.current = true;
    batch.mutate(body);
  };

  useEffect(() => {
    if (!batch.isSuccess || undefined === batch.data) {
      return;
    }
    setResults(batch.data.results);
    setCurrentStepIndex(3);
  }, [batch.isSuccess, batch.data]);

  // A settled batch frees Import again. One that got no response at all also unticks consent and
  // locks Import until a new scan: the server may still be importing, so one click must not be
  // able to send the same list again (see `batchOutcomeUnknown`).
  useEffect(() => {
    if (batch.isSuccess || batch.isError) {
      importInFlight.current = false;
    }
    if (batch.isError && hasNoResponse(batch.error)) {
      setBatchOutcomeUnknown(true);
      setConsentChecked(false);
    }
  }, [batch.isSuccess, batch.isError, batch.data, batch.error]);

  const renderStepContent = (): JSX.Element | null => {
    switch (currentStepIndex) {
      case 0:
        return (
          <SourceStep
            repoUrl={repoUrl}
            onRepoUrlChange={setRepoUrl}
            provider={provider}
            onProviderChange={value => {
              // A provider change invalidates whatever credential was chosen for the old one.
              // Resetting to `null` (undecided) is what stops a github credential id from ever
              // being sent alongside a gitlab connection.
              setProvider(value);
              setServiceTokenId(null);
            }}
            serviceTokenId={serviceTokenId}
            onServiceTokenIdChange={setServiceTokenId}
            onCheck={handleScanRepository}
            isChecking={scan.isPending}
            errorMessage={
              scan.isError
                ? detailOf(scan.error, "The repository could not be scanned.")
                : null
            }
          />
        );
      case 1:
        return null !== scanReport ? (
          <div className="space-y-4">
            <PickTableStep
              rows={scanReport.rows}
              maxBatchImport={scanReport.maxBatchImport}
              latestOnly={latestOnly}
              onLatestOnlyChange={handleLatestOnlyChange}
              isScanning={scan.isPending}
              onSelectionChange={setSelected}
            />
            {scan.isError ? (
              <p className="text-sm text-red-600" data-testid="importPickError">
                {detailOf(scan.error, "The repository could not be scanned.")}
              </p>
            ) : null}
            <div className="flex justify-end">
              <button
                type="button"
                data-testid="importPickContinue"
                disabled={0 === selected.length || scan.isPending}
                onClick={handleContinueToConsent}
                className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600 text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed">
                Continue
              </button>
            </div>
          </div>
        ) : null;
      case 2:
        return null !== scannedSource ? (
          <ConsentStep
            repoUrl={scannedSource.repoUrl}
            items={selected}
            menuGroupId={menuGroupId}
            onMenuGroupIdChange={setMenuGroupId}
            consentChecked={consentChecked}
            onConsentCheckedChange={setConsentChecked}
            onImport={handleImport}
            isImporting={batch.isPending}
            importLocked={batchOutcomeUnknown}
            onScanAgain={() => resetWizard({ keepSource: true })}
            errorMessage={
              batch.isError
                ? hasNoResponse(batch.error)
                  ? BATCH_NO_ANSWER_MESSAGE
                  : detailOf(
                      batch.error,
                      "The processes could not be imported."
                    )
                : null
            }
          />
        ) : null;
      case 3:
        return null !== results ? (
          <ResultsStep
            results={results}
            processNames={processNames}
            onRunAgain={() => resetWizard({ keepSource: true })}
            onDone={() => setOpen(false)}
          />
        ) : null;
      default:
        return null;
    }
  };

  // While a batch runs, closing would lose every per item result: the caller mounts this modal
  // only while it is open, so a close unmounts the wizard and the results with it, and a reopen
  // resets anyway. So every close path is blocked until the batch settles. `disableClose` removes
  // the X and stops the backdrop click; `onBeforeClose` stops the Modal's own Escape handler,
  // which does not consult `disableClose`. The Consent step shows why while it is blocked.
  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Import Process From Git"
      size={ModalSize.Large}
      hasCancelButton={false}
      disableClose={batch.isPending}
      onBeforeClose={() => !batch.isPending}>
      <div className="space-y-4">
        <StepProgress steps={steps} currentStepIndex={currentStepIndex} />
        <div className="mt-6">{renderStepContent()}</div>
      </div>
    </Modal>
  );
};

export default ImportProcessFromGitModal;
